import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Music2, Music4, CheckCircle2, Loader2, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SiteFooter } from "@/components/SiteFooter";
import { Equalizer } from "@/components/Equalizer";
import { Soundboard } from "@/components/Soundboard";

const TRIVIA = [
  "The McDonald's \"I'm Lovin' It\" hook was co-written by Justin Timberlake — and it's worth millions.",
  "\"Nationwide is on your side\" is just 5 notes. People remember it decades later.",
  "Studies show ads with memorable music are 24% more likely to drive purchase intent.",
  "The Kit Kat \"Give me a break\" jingle has been running since 1986. That's the power of a catchy hook.",
  "Local jingles outperform generic ad music on recall by up to 3x in market tests.",
];

const NOTE_ICONS = [Music, Music2, Music4];

export default function RecordingStudio() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [triviaIdx, setTriviaIdx] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [bandPlaying, setBandPlaying] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const bandRef = useRef(null);

  const saveEmail = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val.includes("@") || !val.includes(".")) {
      toast.error("That email doesn't look quite right.");
      return;
    }
    setEmailSaving(true);
    try {
      await api.post(`/jingles/${jobId}/email`, { email: val });
      setEmailSent(true);
      toast.success("Done — your previews will land in your inbox too.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that — try once more.");
    } finally {
      setEmailSaving(false);
    }
  };

  const toggleBand = () => {
    const el = bandRef.current;
    if (!el) return;
    if (bandPlaying) {
      el.pause();
    } else {
      document.querySelectorAll("audio").forEach((a) => a !== el && a.pause());
      el.play();
    }
  };

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/jingles/${jobId}`);
        if (!alive) return;
        setJob(data);
        if (data.status === "ready") {
          setTimeout(() => alive && navigate(`/jingle-preview/${jobId}`), 1800);
          return;
        }
        if (data.status === "failed") return;
        setTimeout(poll, 1200);
      } catch (e) {
        if (alive) setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { alive = false; };
  }, [jobId, navigate]);

  useEffect(() => {
    const t = setInterval(() => setTriviaIdx((i) => (i + 1) % TRIVIA.length), 6000);
    return () => clearInterval(t);
  }, []);

  // Displayed % never falls between polls (the server floors it too); a retry
  // navigates to a new jobId, which resets the floor.
  const progressFloorRef = useRef({ id: jobId, floor: 3 });
  if (progressFloorRef.current.id !== jobId) {
    progressFloorRef.current = { id: jobId, floor: 3 };
  }
  progressFloorRef.current.floor = Math.max(progressFloorRef.current.floor, job?.progress ?? 3);
  const progress = progressFloorRef.current.floor;
  const stepIndex = job?.step_index ?? 0;
  const steps = job?.steps ?? [];
  const ready = job?.status === "ready";
  const failed = job?.status === "failed";
  const recovering = Boolean(job?.recovering) && !ready && !failed;

  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/sites/${job.site_key}/retry`, { job_id: jobId });
      if (data.feedback_dropped) {
        toast.info("The studio couldn't take direction notes this time — re-cutting without them.");
      }
      navigate(`/recording-studio/${data.job_id}`);
    } catch (e) {
      toast.error("Retry hiccuped — give it one more tap.");
      setRetrying(false);
    }
  };

  return (
    <div data-testid="recording-studio-page" className="min-h-screen bg-obsidian text-white relative overflow-hidden">
      <audio
        ref={bandRef}
        src="/listen-in-to-the-band.mp3"
        loop
        preload="none"
        onPlay={() => setBandPlaying(true)}
        onPause={() => setBandPlaying(false)}
      />
      {/* floating notes */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {[...Array(8)].map((_, i) => {
          const Icon = NOTE_ICONS[i % 3];
          return (
            <Icon
              key={i}
              className="absolute float-note text-gold/30"
              style={{ left: `${8 + i * 12}%`, bottom: "-8%", animationDelay: `${i * 1.1}s`, animationDuration: `${6 + (i % 3)}s` }}
            />
          );
        })}
      </div>

      <header className="relative z-10 mx-auto max-w-5xl px-4 sm:px-8 pt-10 flex items-center justify-between">
        <p className="font-display font-extrabold tracking-tight">
          <span className="text-gold">Local</span>Business<span className="text-gold">Jingle</span><span className="text-slate-500">.com</span>
        </p>
        <div data-testid="rec-badge" className="flex items-center gap-2.5 glass-panel rounded-full px-4 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-coral rec-pulse" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral">
            {ready ? "Mastering complete" : failed ? "Snag — retry free" : "Recording in progress"}
          </span>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 sm:px-8 pt-14 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <h1 data-testid="studio-headline" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] max-w-2xl">
            We're recording your song
            <span className="text-gold"> right now</span>
            {job?.business_name ? <> for <span className="text-neon">{job.business_name}</span></> : null}.
          </h1>
          <p className="mt-4 text-lg text-slate-400 max-w-xl">
            The tape is rolling. Vocalists are warming up. Keep this tab open — your
            watermarked preview drops in a few minutes.
          </p>
        </motion.div>

        {/* Enable sound — the band is live on the studio floor */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mt-10 max-w-md"
        >
          <button
            data-testid="listen-in-band-btn"
            onClick={toggleBand}
            className={`w-full glass-panel rounded-2xl p-4 sm:p-5 flex items-center gap-4 transition-colors cursor-pointer text-left ${bandPlaying ? "" : "sound-bob border-gold/40"}`}
          >
            <span className="relative h-12 w-12 shrink-0">
              {!bandPlaying && (
                <>
                  <span className="sound-ring absolute inset-0 rounded-full border-2 border-gold" aria-hidden />
                  <span className="sound-ring absolute inset-0 rounded-full border-2 border-gold" style={{ animationDelay: "0.7s" }} aria-hidden />
                </>
              )}
              <span className={`absolute inset-0 rounded-full flex items-center justify-center transition-all ${bandPlaying ? "bg-coral shadow-[0_0_20px_rgba(255,80,80,0.45)]" : "bg-gradient-to-br from-gold to-[#FB8500] shadow-[0_0_24px_rgba(255,183,3,0.5)]"}`}>
                {bandPlaying ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-black ml-0.5" />}
              </span>
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-display font-bold text-sm sm:text-base">
                {bandPlaying ? "You're listening in" : "Enable Sound"}
              </span>
              <span data-testid="listen-in-band-note" className="block text-xs text-slate-400 mt-0.5">
                {bandPlaying ? "Live from the studio floor — tap to step out" : "Listen while you wait"}
              </span>
            </span>
            <Equalizer playing={bandPlaying} color="#00F0FF" className="h-5 shrink-0" />
          </button>
        </motion.div>

        {/* Jam along — right under Enable Sound */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="mt-6 max-w-md"
        >
          <Soundboard />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 mt-12">
          {/* Cassette + progress */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="lg:col-span-7 glass-panel rounded-3xl p-6 sm:p-10"
          >
            {recovering && (
              <div data-testid="studio-recovering" className="mb-8 rounded-2xl border border-gold/50 bg-gold/10 p-6">
                <p className="font-display font-bold text-lg text-gold mb-2">The band is doing one more pass.</p>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Your takes are getting a little extra studio polish — nothing for you to do.
                  {job?.has_email
                    ? " We'll email you the moment they land, so feel free to close this page."
                    : " Leave your email below and we'll send them the moment they land."}
                </p>
              </div>
            )}
            {failed && (
              <div data-testid="studio-failed" className="mb-8 rounded-2xl border border-coral/50 bg-coral/10 p-6">
                <p className="font-display font-bold text-lg text-coral mb-2">The studio hit a snag mid-take.</p>
                <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                  Happens in the best sessions. Re-cut this batch free — it won't touch
                  your free-takes allowance or any credits.
                </p>
                <button
                  data-testid="studio-retry-btn"
                  onClick={retry}
                  disabled={retrying}
                  className="bg-gradient-to-r from-coral to-[#FF5000] text-white font-extrabold px-6 py-3 rounded-xl hover:scale-105 active:scale-95 transition-transform cursor-pointer disabled:opacity-70 flex items-center gap-2"
                >
                  {retrying && <Loader2 className="h-4 w-4 animate-spin" />}
                  CUT IT AGAIN — FREE
                </button>
              </div>
            )}
            {/* Cassette */}
            <div className="rounded-2xl border border-white/10 bg-ink p-6 sm:p-8 mb-8 relative overflow-hidden">
              <div className="flex items-center justify-between gap-6">
                <div className="relative h-20 w-20 sm:h-24 sm:w-24 rounded-full border-4 border-white/15 flex items-center justify-center reel-spin shrink-0">
                  {[...Array(6)].map((_, i) => (
                    <span key={i} className="absolute h-full w-1 bg-white/10" style={{ transform: `rotate(${i * 30}deg)` }} />
                  ))}
                  <span className="h-4 w-4 rounded-full bg-gold relative z-10" />
                </div>
                <div className="flex-1 h-10 sm:h-12 rounded-lg bg-obsidian/80 border border-white/10 relative overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-gold/30 to-coral/30"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.8 }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Equalizer playing={!ready} color="#FFB703" className="h-6" />
                  </div>
                </div>
                <div className="relative h-20 w-20 sm:h-24 sm:w-24 rounded-full border-4 border-white/15 flex items-center justify-center reel-spin shrink-0" style={{ animationDirection: "reverse" }}>
                  {[...Array(6)].map((_, i) => (
                    <span key={i} className="absolute h-full w-1 bg-white/10" style={{ transform: `rotate(${i * 30}deg)` }} />
                  ))}
                  <span className="h-4 w-4 rounded-full bg-coral relative z-10" />
                </div>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-4 text-center">
                LBJ Studios · Session Tape · {job?.song_title ?? "Untitled Jingle"}
              </p>
            </div>

            {/* Progress */}
            <div className="flex items-end justify-between mb-3">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Session progress</span>
              <span data-testid="progress-percent" className="font-display text-4xl font-extrabold text-gold">{progress}%</span>
            </div>
            <div data-testid="progress-bar" className="h-3 rounded-full bg-white/10 overflow-hidden mb-4">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-gold via-[#FB8500] to-coral"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
            {job?.live_step && !failed && (
              <p data-testid="live-step" className="font-mono text-xs text-neon mb-4 tracking-wider">
                ▸ {job.live_step}
              </p>
            )}
            <div className="mb-4" />

            {/* Steps */}
            <ul className="space-y-3.5">
              {steps.map((step, i) => {
                const done = i < stepIndex || ready;
                const active = i === stepIndex && !ready;
                return (
                  <li key={i} data-testid={`studio-step-${i}`} className={`flex items-center gap-3.5 text-sm sm:text-base transition-colors ${done ? "text-slate-300" : active ? "text-white" : "text-slate-600"}`}>
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                    ) : active ? (
                      <Loader2 className="h-5 w-5 text-gold animate-spin shrink-0" />
                    ) : (
                      <span className="h-5 w-5 rounded-full border border-white/15 shrink-0" />
                    )}
                    {step}
                  </li>
                );
              })}
            </ul>

            <AnimatePresence>
              {ready && (
                <motion.p
                  data-testid="studio-ready-msg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 font-display font-bold text-lg text-emerald-400"
                >
                  Your jingle is mixed, mastered & watermarked. Rolling out the red carpet...
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Trivia + email capture */}
          <div className="lg:col-span-5 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="glass-panel rounded-2xl p-6 min-h-[148px]"
            >
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-neon mb-3">Did you know?</p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={triviaIdx}
                  data-testid="trivia-card"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4 }}
                  className="text-base text-slate-300 leading-relaxed"
                >
                  {TRIVIA[triviaIdx]}
                </motion.p>
              </AnimatePresence>
            </motion.div>
            {!job?.has_email && !emailSent && !ready && !failed && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.7 }}
                className="glass-panel rounded-2xl p-6"
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-2">Optional</p>
                <p className="font-display font-bold text-base sm:text-lg">Heading out before it's done?</p>
                <p className="text-xs sm:text-sm text-slate-400 mt-1.5 mb-4 leading-relaxed">
                  Leave your email and we'll send your previews the second they're ready.
                </p>
                <form onSubmit={saveEmail} className="flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid="studio-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbusiness.com"
                    className="flex-1 rounded-xl border border-white/15 bg-obsidian/70 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-gold"
                  />
                  <button
                    data-testid="studio-email-btn"
                    type="submit"
                    disabled={emailSaving}
                    className="shrink-0 bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold text-sm px-5 py-3 rounded-xl hover:scale-[1.03] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {emailSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                    EMAIL ME MY JINGLES
                  </button>
                </form>
              </motion.div>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
